import { TriangleAlert } from 'lucide-react'
import dayjs from 'dayjs'
import RelativeTime from 'dayjs/plugin/relativeTime'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import LinkButton from '@/components/ui/link-button'
import { cn } from '@/lib/utils'
import { difficultiesLabels } from 'app/frontend/types/custom/Difficulty'
import Difficulty from 'app/frontend/types/custom/Difficulty'
import RaidPlan from 'app/frontend/types/custom/RaidPlan'
import TeamPlanCompositionSummary from './TeamPlanCompositionSummary'
import DefaultPlanStar from './DefaultPlanStar'
import { useIsDefaultRaidPlan } from './defaultRaidPlan'
import Card from 'app/frontend/base/components/Card'

dayjs.extend(RelativeTime)

type RaidPlanCandidatePickerProps = {
  plans: RaidPlan[]
  selectedPlanId?: number | null
  linkedPlanIds?: number[]
  warningDifficulty?: Difficulty
  showOwnerBadge?: boolean
  teamPath: string
  onSelect: (plan: RaidPlan) => void
  onUnlink?: (plan: RaidPlan) => void
  emptyLabel?: string
  emptyActionLabel?: string
  emptyActionPath?: string
  cardClassName?: string
}

const RaidPlanCandidatePicker = ({
  plans,
  selectedPlanId,
  linkedPlanIds = [],
  warningDifficulty,
  showOwnerBadge = false,
  teamPath,
  onSelect,
  onUnlink,
  emptyLabel = 'No raid plans found for this encounter',
  emptyActionLabel = 'View plans',
  emptyActionPath,
  cardClassName,
}: RaidPlanCandidatePickerProps) => {
  const { t } = useTranslation()
  const isDefaultRaidPlan = useIsDefaultRaidPlan()
  const linkedPlanIdSet = new Set(linkedPlanIds)

  if (!plans.length) {
    return (
      <>
        <p className='text-sm text-muted-foreground'>{emptyLabel}</p>
        {emptyActionPath ? (
          <LinkButton to={emptyActionPath} size='sm' className='w-full'>
            {emptyActionLabel}
          </LinkButton>
        ) : null}
      </>
    )
  }

  return (
    <>
      {plans.map((plan) => {
        const hasDifficultyWarning =
          warningDifficulty !== undefined &&
          plan.difficulty !== warningDifficulty
        const isPersonal = plan.ownerType === 'User'
        const viewBase = isPersonal ? '/profile' : teamPath
        const isLinked = linkedPlanIdSet.has(plan.id)
        const isSelected = plan.id === selectedPlanId
        const canSelect = !isLinked || isSelected

        return (
          <Card
            key={`${plan.ownerType}-${plan.id}`}
            role={canSelect ? 'button' : undefined}
            tabIndex={canSelect ? 0 : undefined}
            className={cn(
              'w-full justify-between flex flex-col gap-2 p-3 border transition-colors',
              isSelected || isLinked ? 'border-primary' : 'border-border',
              canSelect
                ? 'cursor-pointer hover:border-primary'
                : 'cursor-default',
              cardClassName ?? 'h-fit',
            )}
            onClick={() => {
              if (!canSelect || isLinked) return
              onSelect(plan)
            }}
            onKeyDown={(event) => {
              if (!canSelect || isLinked) return
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                onSelect(plan)
              }
            }}
          >
            <div className='flex items-center justify-between gap-2 w-full'>
              <div className='flex items-center gap-2 min-w-0'>
                {isLinked ? (
                  <Badge variant='success' className='shrink-0'>
                    Linked
                  </Badge>
                ) : null}
                {hasDifficultyWarning ? (
                  <Badge variant='warning' className='gap-1'>
                    <TriangleAlert className='h-3 w-3' />
                    {difficultiesLabels[plan.difficulty]}
                  </Badge>
                ) : null}
                {isDefaultRaidPlan(plan) ? <DefaultPlanStar /> : null}
                <div className='truncate font-medium'>{plan.name}</div>
                {showOwnerBadge ? (
                  <Badge variant={isPersonal ? 'secondary' : 'outline'}>
                    {isPersonal
                      ? t('dashboard.team.plans.owner-badge.personal')
                      : t('dashboard.team.plans.owner-badge.team')}
                  </Badge>
                ) : null}
              </div>

              <div className='flex items-center gap-2'>
                <div className='text-xs text-muted-foreground whitespace-nowrap'>
                  Created {dayjs(plan.createdAt).fromNow()}
                </div>

                {isLinked && onUnlink ? (
                  <Button
                    type='button'
                    variant='outline'
                    size='xs'
                    onClick={(event) => {
                      event.stopPropagation()
                      onUnlink(plan)
                    }}
                  >
                    Unlink
                  </Button>
                ) : null}

                <LinkButton
                  to={`${viewBase}/events/plans/${plan.id}`}
                  target='_blank'
                  size='xs'
                  onClick={(event) => event.stopPropagation()}
                >
                  view
                </LinkButton>
              </div>
            </div>
            <TeamPlanCompositionSummary
              timeline={plan.timeline}
              className='w-full flex-1'
            />
          </Card>
        )
      })}
    </>
  )
}

export default RaidPlanCandidatePicker
